import { Box } from '@mui/material';
import { SortableContext, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import AppIcon from './AppIcon';
import { LAUNCHER_GRID } from '../../../utils/launcherGrid';

function emptySlotProps(clickable, onEmptyClick, slotIndex) {
  if (!clickable) return {};
  return {
    role: 'button',
    tabIndex: 0,
    'aria-label': 'Add button',
    onClick: () => onEmptyClick(slotIndex),
    onKeyDown: (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      onEmptyClick(slotIndex);
    },
  };
}

function StaticPlaceholder({ showGrid, dark, clickable, onEmptyClick, slotIndex }) {
  return (
    <Box
      {...emptySlotProps(clickable, onEmptyClick, slotIndex)}
      sx={{
        width: '100%',
        height: '100%',
        borderRadius: 2,
        border: showGrid ? '1px dashed' : 'none',
        borderColor: showGrid
          ? dark
            ? 'rgba(255, 255, 255, 0.2)'
            : 'rgba(0, 0, 0, 0.2)'
          : 'transparent',
        boxSizing: 'border-box',
        cursor: clickable ? 'pointer' : 'default',
        '&:hover': clickable ? { bgcolor: 'action.hover' } : undefined,
      }}
    />
  );
}

export default function IconGrid({
  slots,
  editMode,
  canDrag = editMode,
  isLayoutDragging = false,
  suppressActivation = false,
  onLongPress,
  onIconClick,
  onEditRequest,
  onDeleteRequest,
  onAddRequest,
  onEmptyClick,
  canAdd,
  gridMax,
  dark,
  columns = 4,
  iconSize = 'medium',
  gap = 2,
  showGrid = false,
  previewMode = false,
  labelOverBackground = false,
  fluidCells = false,
  tenantId = '',
}) {
  const total = Math.max(gridMax || slots.length, slots.length);
  const items = Array.from({ length: total }).map((_, idx) => slots[idx]?.id || `slot-${idx}`);

  const safeColumns = Math.max(columns || 1, 1);
  const rowCount = Math.max(Math.ceil(total / safeColumns), 1);

  const previewSizeScale = {
    small: 0.55,
    medium: 0.7,
    large: 0.85,
    xlarge: 1,
  };
  const previewScale = previewSizeScale[iconSize] || previewSizeScale.medium;
  const previewIconPercent = `${previewScale * 100}%`;

  if (previewMode) {
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${safeColumns}, minmax(0, 1fr))`,
          gridTemplateRows: `repeat(${rowCount}, minmax(0, 1fr))`,
          gap,
          width: '100%',
          height: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden',
          alignContent: 'stretch',
        }}
      >
        {Array.from({ length: total }).map((_, idx) => (
          <Box
            key={`preview-cell-${idx}`}
            sx={{
              width: '100%',
              height: '100%',
              minWidth: 0,
              minHeight: 0,
              borderRadius: 2,
              border: showGrid ? '1px dashed' : 'none',
              borderColor: showGrid
                ? dark
                  ? 'rgba(255, 255, 255, 0.25)'
                  : 'rgba(0, 0, 0, 0.2)'
                : 'transparent',
              boxSizing: 'border-box',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                width: previewIconPercent,
                aspectRatio: '1 / 1',
                maxWidth: '80%',
                maxHeight: '80%',
                borderRadius: 2,
                bgcolor: dark ? 'primary.main' : 'primary.light',
                opacity: 0.6,
              }}
            />
          </Box>
        ))}
      </Box>
    );
  }

  function SortableTile({ icon }) {
    const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({
      id: icon.id,
      disabled: !canDrag,
    });
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      width: '100%',
      height: '100%',
      touchAction: canDrag ? 'none' : 'manipulation',
      cursor: canDrag ? (isDragging ? 'grabbing' : 'grab') : 'pointer',
      zIndex: isDragging ? 10 : 'auto',
      willChange: 'transform',
      opacity: isDragging ? 0.55 : 1,
      boxShadow: isDragging ? '0 12px 28px rgba(0,0,0,0.28)' : 'none',
      border: showGrid ? '1px dashed' : 'none',
      borderColor: showGrid
        ? dark
          ? 'rgba(255, 255, 255, 0.2)'
          : 'rgba(0, 0, 0, 0.2)'
        : 'transparent',
      borderRadius: 8,
      boxSizing: 'border-box',
      overflow: 'hidden',
      minHeight: 0,
    };
    return (
      <div ref={setNodeRef} style={style}>
        <AppIcon
          icon={icon}
          editMode={editMode}
          suppressActivation={suppressActivation || isDragging || isLayoutDragging}
          onLongPress={onLongPress}
          onClick={() => onIconClick(icon)}
          onEditRequest={onEditRequest}
          onDeleteRequest={onDeleteRequest}
          dragHandleProps={{ ...attributes, ...listeners }}
          dark={dark}
          iconSize={iconSize}
          labelOverBackground={labelOverBackground}
          tenantId={tenantId}
        />
      </div>
    );
  }

  function SortablePlaceholder({ id, slotIndex }) {
    const { setNodeRef, isOver } = useDroppable({ id });
    const showSlotChrome = showGrid || editMode || isLayoutDragging;
    const clickable = Boolean(canAdd && onEmptyClick) && !suppressActivation && !isLayoutDragging;
    return (
      <Box
        ref={setNodeRef}
        {...emptySlotProps(clickable, onEmptyClick, slotIndex)}
        sx={{
          width: '100%',
          height: '100%',
          borderRadius: 2,
          border: showSlotChrome ? '1px dashed' : 'none',
          borderColor: isOver
            ? 'primary.main'
            : showGrid
              ? dark
                ? 'rgba(255, 255, 255, 0.2)'
                : 'rgba(0, 0, 0, 0.2)'
              : 'divider',
          bgcolor: isOver ? 'action.selected' : isLayoutDragging ? 'action.hover' : 'transparent',
          outline: isOver ? '2px solid' : 'none',
          outlineColor: isOver ? 'primary.main' : 'transparent',
          transition:
            'border-color 120ms ease, background-color 120ms ease, outline-color 120ms ease',
          boxSizing: 'border-box',
          cursor: clickable ? 'pointer' : 'default',
          '&:hover': clickable && !isOver ? { bgcolor: 'action.hover' } : undefined,
        }}
      />
    );
  }

  const gridKey = items.join('|');
  return (
    <SortableContext items={items} strategy={rectSortingStrategy}>
      <Box
        key={gridKey}
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${safeColumns}, minmax(${fluidCells ? 0 : LAUNCHER_GRID.MIN_CELL_WIDTH}px, 1fr))`,
          gridTemplateRows: `repeat(${rowCount}, minmax(${fluidCells ? 0 : LAUNCHER_GRID.MIN_CELL_HEIGHT}px, 1fr))`,
          gap,
          px: { xs: 1, sm: 2 },
          py: { xs: 1, sm: 2 },
          height: '100%',
          boxSizing: 'border-box',
          width: '100%',
          maxWidth: '100%',
          margin: '0 auto',
          overflow: 'hidden',
        }}
      >
        {Array.from({ length: total }).map((_, idx) => {
          const icon = slots[idx] || null;
          if (icon) {
            return <SortableTile key={icon.id} icon={icon} />;
          }
          const clickable = Boolean(canAdd && onEmptyClick) && !suppressActivation && !isLayoutDragging;
          return canDrag ? (
            <SortablePlaceholder key={`slot-${idx}`} id={`slot-${idx}`} slotIndex={idx} />
          ) : (
            <StaticPlaceholder
              key={`slot-${idx}`}
              showGrid={showGrid}
              dark={dark}
              clickable={clickable}
              onEmptyClick={onEmptyClick}
              slotIndex={idx}
            />
          );
        })}
      </Box>
    </SortableContext>
  );
}
